import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Shield, FileText, RefreshCw, Save, RotateCcw, Eye, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LegalPagesManagement = () => {
  const { getAuthHeader } = useAuth();
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy_policy');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ title: '', content: '' });

  const pageConfig = {
    privacy_policy: {
      label: 'Privacy Policy',
      icon: <Shield className="h-4 w-4" />,
      description: 'Data collection and usage policy'
    },
    terms_of_use: {
      label: 'Terms of Use',
      icon: <FileText className="h-4 w-4" />,
      description: 'Service usage terms and conditions'
    },
    refund_policy: {
      label: 'Refund Policy',
      icon: <RefreshCw className="h-4 w-4" />,
      description: 'Payment refund policy (Required for payment gateway)'
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legal-pages/my-school`, {
        headers: getAuthHeader()
      });
      setPages(res.data);
      
      // Set initial edit data
      if (res.data[activeTab]) {
        setEditData({
          title: res.data[activeTab].title,
          content: res.data[activeTab].content
        });
      }
    } catch (error) {
      console.error('Failed to load pages:', error);
      toast.error('Failed to load legal pages');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setEditMode(false);
    if (pages[tab]) {
      setEditData({
        title: pages[tab].title,
        content: pages[tab].content
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/legal-pages/my-school/${activeTab}`,
        editData,
        { headers: getAuthHeader() }
      );
      toast.success('Page saved successfully');
      setEditMode(false);
      fetchPages();
    } catch (error) {
      toast.error('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset this page to default? This will remove all your customizations.')) {
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/legal-pages/my-school/${activeTab}/reset`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Page reset to default');
      fetchPages();
    } catch (error) {
      toast.error('Failed to reset page');
    }
  };

  const currentPage = pages[activeTab] || {};

  if (loading) {
    return (
      <DashboardLayout title="Legal Pages">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Legal Pages Management">
      <div className="space-y-6">
        {/* Info Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-700">
              <strong>Payment Gateway Requirement:</strong> Privacy Policy, Terms of Use, and Refund Policy pages are mandatory for online payment integration. 
              These pages will be accessible from your school's login page footer.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Manage Legal Pages</CardTitle>
            <CardDescription>
              Customize your school's legal pages or use the default templates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="mb-6">
                {Object.entries(pageConfig).map(([key, config]) => (
                  <TabsTrigger key={key} value={key} className="flex items-center gap-2">
                    {config.icon}
                    {config.label}
                    {pages[key]?.is_custom && (
                      <Badge variant="secondary" className="ml-1 text-xs">Custom</Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.entries(pageConfig).map(([key, config]) => (
                <TabsContent key={key} value={key} className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{config.label}</h3>
                      <p className="text-sm text-muted-foreground">{config.description}</p>
                    </div>
                    <div className="flex gap-2">
                      {!editMode ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                            Edit
                          </Button>
                          {pages[key]?.is_custom && (
                            <Button variant="ghost" size="sm" onClick={handleReset}>
                              <RotateCcw className="h-4 w-4 mr-1" /> Reset to Default
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={handleSave} disabled={saving}>
                            <Save className="h-4 w-4 mr-1" />
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {editMode ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Page Title</Label>
                        <Input
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          placeholder="Page title"
                        />
                      </div>
                      <div>
                        <Label>Content (Markdown supported)</Label>
                        <Textarea
                          value={editData.content}
                          onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                          placeholder="Page content in Markdown format"
                          className="min-h-[400px] font-mono text-sm"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Tip: Use Markdown formatting - ## for headings, **bold**, *italic*, - for lists
                      </div>
                    </div>
                  ) : (
                    <div className="border rounded-lg p-6 bg-gray-50">
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{currentPage.content || ''}</ReactMarkdown>
                      </div>
                      {currentPage.updated_at && (
                        <p className="text-xs text-muted-foreground mt-4 pt-4 border-t">
                          Last updated: {new Date(currentPage.updated_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>

        {/* Preview Links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="h-4 w-4" /> Preview Links
            </CardTitle>
            <CardDescription>
              These links will appear in your school's login page footer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <a 
                href={`${window.location.origin}/legal/dps/privacy-policy`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                Privacy Policy <ExternalLink className="h-3 w-3" />
              </a>
              <a 
                href={`${window.location.origin}/legal/dps/terms-of-use`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                Terms of Use <ExternalLink className="h-3 w-3" />
              </a>
              <a 
                href={`${window.location.origin}/legal/dps/refund-policy`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                Refund Policy <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default LegalPagesManagement;
